<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useMapGetter, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { helpers, required } from '@vuelidate/validators';
import { alphanumericUnderscore } from 'dashboard/routes/dashboard/settings/wgptBots/edit/tools/helpers/rulesHelper';
import useVuelidate from '@vuelidate/core';
import { useRoute, useRouter } from 'vue-router';

import { OnClickOutside } from '@vueuse/components';
import DropdownMenu from 'next/dropdown-menu/DropdownMenu.vue';
import Spinner from 'next/spinner/Spinner.vue';
import Button from 'next/button/Button.vue';
import Breadcrumb from 'next/breadcrumb/Breadcrumb.vue';
import Editor from 'next/wgpt/Automation/editor/Index.vue';
import ToolEditorLayout from './ToolEditorLayout.vue';
import ToolEditorSettings from './ToolEditorSettings.vue';
import TestToolDialog from '../test/TestToolDialog.vue';

const { t } = useI18n();
const getters = useStoreGetters();
const store = useStore();
const route = useRoute();
const router = useRouter();

const testToolDialogRef = ref(null);
const showActionMenu = ref(false);

const serverId = computed(() => Number(route.params.serverId));
const toolId = computed(() => Number(route.params.toolId));
const server = computed(() =>
  getters['wgptMcpServers/getServer'].value(serverId.value)
);
const tools = computed(() =>
  getters['wgptMcpServers/getTools'].value(serverId.value)
);
const tool = computed(() =>
  getters['wgptMcpServers/getTool'].value(serverId.value, toolId.value)
);
const uiFlags = useMapGetter('wgptMcpServers/getUIFlags');

const isUniqueName = value => {
  if (!value) return true;
  return !tools.value.some(
    ({ name: toolName, id }) => toolName === value && id !== toolId.value
  );
};

const state = reactive({});
const rules = {
  value: {
    name: {
      required,
      alphanumericUnderscore,
      isUnique: helpers.withMessage(
        () =>
          t(
            'WGPT_MCP_SERVERS.EDIT.TOOLS.EDITOR.SETTINGS.FORM.NAME.ERROR_UNIQUE'
          ),
        isUniqueName
      ),
    },
    description: {
      required,
    },
  },
};
const v$ = useVuelidate(rules, state);

const hasPendingChanges = computed(() => {
  if (!tool.value || !state.value) return false;

  return (
    state.value.name !== tool.value.name ||
    state.value.description !== tool.value.description ||
    JSON.stringify(state.value.steps) !== JSON.stringify(tool.value.steps) ||
    JSON.stringify(state.value.trigger) !== JSON.stringify(tool.value.trigger)
  );
});

const isLoading = computed(
  () => server.value.id !== serverId.value || tool.value.id !== toolId.value
);

const breadcrumbItems = computed(() => {
  const items = [
    { label: server.value?.name || '...' },
    {
      label: t('WGPT_MCP_SERVERS.EDIT.TOOLS.EDITOR.BREADCRUMB.TOOLS'),
      link: '#',
    },
  ];
  if (tool.value?.name) items.push({ label: tool.value.name });
  return items;
});

const selected = computed(() => route.query.selected);

const settingsHasError = computed(() =>
  v$.value.$errors.some(error =>
    ['name', 'description'].includes(error.$property)
  )
);

const isToolActive = computed(() => tool.value?.active);

const actionMenuItems = computed(() => [
  {
    label: t('WGPT_MCP_SERVERS.EDIT.TOOLS.EDITOR.HEADER.DEACTIVATE_BUTTON'),
    value: 'deactivate',
    action: 'deactivate',
    disabled:
      isLoading.value ||
      uiFlags.value.isUpdating ||
      hasPendingChanges.value ||
      !isToolActive.value,
  },
  {
    label: t('WGPT_MCP_SERVERS.EDIT.TOOLS.EDITOR.HEADER.TEST_BUTTON'),
    value: 'test',
    action: 'test',
    disabled: uiFlags.value.isUpdating || hasPendingChanges.value,
  },
]);

const handleSaveTool = async () => {
  try {
    if (v$.value.$invalid) {
      v$.value.$touch();
      throw new Error(
        t('WGPT_MCP_SERVERS.EDIT.TOOLS.EDITOR.API.ERROR_MESSAGE')
      );
    }

    const updatedTool = await store.dispatch('wgptMcpServers/updateTool', {
      serverId: serverId.value,
      id: toolId.value,
      name: state.value.name,
      description: state.value.description,
      trigger: state.value.trigger,
      steps: state.value.steps,
    });
    useAlert(t('WGPT_MCP_SERVERS.EDIT.TOOLS.EDITOR.API.SUCCESS_MESSAGE'));

    if (updatedTool) {
      state.value = {
        name: updatedTool.name,
        description: updatedTool.description,
        trigger: updatedTool.trigger,
        steps: updatedTool.steps,
      };
    }
  } catch (error) {
    useAlert(
      error?.message ||
        t('WGPT_MCP_SERVERS.EDIT.TOOLS.EDITOR.API.ERROR_MESSAGE')
    );
  }
};

const handleTestTool = () => {
  testToolDialogRef.value?.open(state.value);
};

const toggleActivate = async isActive => {
  if (isToolActive.value === isActive) return;

  try {
    await store.dispatch('wgptMcpServers/updateTool', {
      serverId: serverId.value,
      id: toolId.value,
      active: isActive,
    });

    // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
    const successMessage = t(
      `WGPT_MCP_SERVERS.EDIT.TOOLS.EDIT_TOOL.API.${isActive ? 'ACTIVATED' : 'DEACTIVATED'}_SUCCESSFUL`
    );
    useAlert(successMessage);
  } catch (error) {
    useAlert(
      error?.message ||
        t('WGPT_MCP_SERVERS.EDIT.TOOLS.EDIT_TOOL.API.ERROR_MESSAGE')
    );
  }
};

const handleMenuAction = ({ action }) => {
  showActionMenu.value = false;
  if (action === 'deactivate') toggleActivate(false);
  if (action === 'test') handleTestTool();
};

watch(
  tool,
  (newTool, oldTool = {}) => {
    if (Object.keys(oldTool || {}).length) return;
    state.value = {
      name: newTool.name,
      description: newTool.description,
      trigger: newTool.trigger,
      steps: newTool.steps,
    };
  },
  { immediate: true }
);

watch(selected, () => {
  v$.value.$touch();
});

const goToToolsList = () => {
  router.replace({
    name: 'wgpt_mcp_servers_edit',
    params: {
      accountId: route.params.accountId,
      serverId: serverId.value,
      tab: 'tools',
    },
  });
};

onMounted(() => {
  if (server.value.id !== serverId.value) {
    store.dispatch('wgptMcpServers/show', serverId.value);
  }
});
</script>

<template>
  <ToolEditorLayout>
    <template #start>
      <div class="flex items-center gap-5">
        <Breadcrumb :items="breadcrumbItems" @click="goToToolsList" />
      </div>
    </template>
    <template #end>
      <Button
        :label="t('WGPT_MCP_SERVERS.EDIT.TOOLS.EDITOR.HEADER.SAVE_BUTTON')"
        :is-loading="uiFlags.isUpdating"
        :disabled="!hasPendingChanges || uiFlags.isUpdating"
        size="sm"
        @click="handleSaveTool"
      />
      <div class="flex items-center">
        <Button
          :label="
            t('WGPT_MCP_SERVERS.EDIT.TOOLS.EDITOR.HEADER.ACTIVATE_BUTTON')
          "
          size="sm"
          class="ltr:rounded-r-none rtl:rounded-l-none"
          :disabled="
            isLoading || uiFlags.isUpdating || hasPendingChanges || isToolActive
          "
          @click="toggleActivate(true)"
        />
        <div class="relative">
          <OnClickOutside @trigger="showActionMenu = false">
            <Button
              icon="i-lucide-chevron-down"
              size="sm"
              class="ltr:rounded-l-none rtl:rounded-r-none"
              @click.stop="showActionMenu = !showActionMenu"
            />
            <DropdownMenu
              v-if="showActionMenu"
              :menu-items="actionMenuItems"
              class="mt-2 ltr:right-0 rtl:left-0 top-full"
              @action="handleMenuAction($event)"
            />
          </OnClickOutside>
        </div>
      </div>

      <router-link :to="{ query: { selected: 'settings' } }" replace>
        <Button
          variant="ghost"
          :color="settingsHasError ? 'ruby' : 'slate'"
          icon="i-lucide-bolt"
          size="sm"
          :class="{
            'outline outline-1 outline-offset-1 outline-n-brand':
              selected === 'settings' && !settingsHasError,
            'outline outline-1 outline-offset-1 outline-n-ruby-10':
              settingsHasError,
          }"
          :title="
            t('WGPT_MCP_SERVERS.EDIT.TOOLS.EDITOR.HEADER.SETTINGS_BUTTON')
          "
        />
      </router-link>
    </template>
    <div
      v-if="isLoading"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>
    <Editor
      v-else
      v-model="state.value"
      :dirty="hasPendingChanges"
      class="h-full border-t border-n-weak"
      disable-trigger
    >
      <template #customSidebarSettings>
        <ToolEditorSettings
          v-model="state.value"
          :server="server"
          :tool="tool"
        />
      </template>
    </Editor>

    <TestToolDialog ref="testToolDialogRef" :server-id="serverId" />
  </ToolEditorLayout>
</template>
